import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, X, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sticky Market CTA Button
export const StickyMarketButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: 20 }}
          className="fixed bottom-6 right-6 z-50"
        >
          <Link
            to="/market"
            className="flex items-center gap-2 bg-gradient-to-r from-[#D4AF37] to-[#c9a432] text-[#1A3625] px-4 py-3 sm:px-6 sm:py-4 rounded-full font-semibold shadow-2xl hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all duration-300 hover:scale-105 text-sm sm:text-base"
            data-testid="sticky-market-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            <span className="hidden sm:inline">Markete Git</span>
            <span className="sm:hidden">Market</span>
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Floating Product Preview
export const FloatingProductPreview = () => {
  const [product, setProduct] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const fetchRandomProduct = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products`);
        const products = response.data;
        if (products.length > 0) {
          const randomProduct = products[Math.floor(Math.random() * products.length)];
          setProduct(randomProduct);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }
    };

    fetchRandomProduct();

    // Show after 10 seconds
    const showTimer = setTimeout(() => {
      if (!dismissed) setIsVisible(true);
    }, 10000);

    // Hide after 20 seconds
    const hideTimer = setTimeout(() => {
      setIsVisible(false);
    }, 25000);

    return () => {
      clearTimeout(showTimer);
      clearTimeout(hideTimer);
    };
  }, [dismissed]);

  if (!product || dismissed) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, x: -100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          className="fixed bottom-24 left-6 z-40 w-80"
          data-testid="floating-product"
        >
          <div className="card-glass p-5 shadow-2xl relative">
            <button
              onClick={() => { setIsVisible(false); setDismissed(true); }}
              className="absolute -top-2 -right-2 w-7 h-7 bg-white rounded-full shadow flex items-center justify-center text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
            
            <p className="text-base text-[#D4AF37] font-semibold mb-3">Sizin için öneri</p>
            
            <div className="flex gap-4 items-center">
              <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-[#1A3625] text-base truncate">{product.name}</h4>
                <p className="text-[#D4AF37] font-bold text-lg">₺{product.price}</p>
              </div>
            </div>
            
            <Link
              to={`/market/urun/${product.id}`}
              className="mt-4 w-full bg-[#1A3625] text-white text-base py-3 rounded-full flex items-center justify-center gap-1 font-medium hover:bg-[#1A3625]/90 transition-colors"
            >
              İncele <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Announcement Banner
export const AnnouncementBanner = () => {
  const [isVisible, setIsVisible] = useState(true);
  
  const announcements = [
    "🍯 Taze Bahar Balı Geldi! Sınırlı Stok",
    "🚚 500₺ Üzeri Siparişlerde Ücretsiz Kargo",
    "🎁 Hediye Kutularında %10 İndirim",
    "👩‍🌾 Yeni Üreticimiz Ayşe Hanım'ın Ürünleri Markette"
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [announcements.length]);

  if (!isVisible) return null;

  return (
    <div className="bg-[#1A3625] text-white py-2 relative overflow-hidden" data-testid="announcement-banner">
      <div className="container-custom flex items-center justify-center">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-center gap-4"
          >
            <span className="text-base font-medium">{announcements[currentIndex]}</span>
            <Link 
              to="/market" 
              className="text-[#D4AF37] text-base font-semibold hover:underline flex items-center gap-1"
            >
              Alışverişe Başla <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </AnimatePresence>
        
        <button
          onClick={() => setIsVisible(false)}
          className="absolute right-4 text-white/60 hover:text-white"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

// WhatsApp Order Button
export const WhatsAppButton = ({ productName, productPrice }) => {
  const phoneNumber = "905321234567"; // KOSAD telefon numarası
  const message = productName 
    ? `Merhaba, ${productName} (₺${productPrice}) ürününü sipariş etmek istiyorum.`
    : "Merhaba, sipariş vermek istiyorum.";
  
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

  return (
    <a
      href={whatsappUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 rounded-full font-medium hover:bg-[#20bd5a] transition-colors shadow-lg"
      data-testid="whatsapp-order-btn"
    >
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      WhatsApp ile Sipariş Ver
    </a>
  );
};

// Social Proof Component
export const SocialProof = ({ productId }) => {
  const [viewCount, setViewCount] = useState(0);
  const [recentBuyers, setRecentBuyers] = useState(0);

  useEffect(() => {
    // Simüle edilmiş veriler (gerçek uygulamada API'den gelir)
    setViewCount(Math.floor(Math.random() * 50) + 10);
    setRecentBuyers(Math.floor(Math.random() * 10) + 2);
  }, [productId]);

  return (
    <div className="flex flex-wrap gap-4 text-base" data-testid="social-proof">
      <div className="flex items-center gap-2 bg-[#F9F8F6] px-4 py-2 rounded-full">
        <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-[#5C5C5C]">Bugün <strong className="text-[#1A3625]">{viewCount} kişi</strong> inceledi</span>
      </div>
      <div className="flex items-center gap-2 bg-[#D4AF37]/10 px-4 py-2 rounded-full">
        <span className="text-[#5C5C5C]">Son 7 günde <strong className="text-[#D4AF37]">{recentBuyers} satış</strong></span>
      </div>
    </div>
  );
};

// Season Highlight Badge
export const SeasonHighlight = () => {
  return (
    <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4AF37]/20 to-[#D4AF37]/10 border border-[#D4AF37]/30 px-4 py-2 rounded-full" data-testid="season-highlight">
      <span className="text-lg">🌸</span>
      <span className="text-base font-medium text-[#1A3625]">Bahar Balı Sezonu Başladı!</span>
    </div>
  );
};

// Mini Cart Preview
export const MiniCartPreview = ({ isOpen, onClose, items, total }) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          className="absolute top-full right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-[#E5DAC8] overflow-hidden z-50"
          data-testid="mini-cart-preview"
        >
          <div className="p-4 border-b border-[#E5DAC8]">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-[#1A3625]">Sepetiniz</h4>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          
          <div className="max-h-64 overflow-y-auto p-4 space-y-3">
            {items.length === 0 ? (
              <p className="text-center text-[#5C5C5C] py-4">Sepetiniz boş</p>
            ) : (
              items.slice(0, 3).map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-medium text-[#1A3625] truncate">{item.name}</p>
                    <p className="text-base text-[#5C5C5C]">{item.quantity} x ₺{item.price}</p>
                  </div>
                  <p className="text-base font-semibold text-[#D4AF37]">₺{item.price * item.quantity}</p>
                </div>
              ))
            )}
            {items.length > 3 && (
              <p className="text-sm text-center text-[#5C5C5C]">+{items.length - 3} ürün daha</p>
            )}
          </div>
          
          {items.length > 0 && (
            <div className="p-4 bg-[#F9F8F6] border-t border-[#E5DAC8]">
              <div className="flex justify-between mb-3">
                <span className="font-medium text-[#1A3625]">Toplam</span>
                <span className="font-bold text-[#D4AF37]">₺{total.toLocaleString()}</span>
              </div>
              <Link
                to="/market/sepet"
                className="block w-full bg-[#1A3625] text-white text-center py-3 rounded-full font-medium hover:bg-[#1A3625]/90 transition-colors"
                onClick={onClose}
              >
                Sepete Git
              </Link>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Reorder Button
export const ReorderButton = ({ lastOrder }) => {
  if (!lastOrder) return null;

  return (
    <button
      className="flex items-center gap-2 bg-[#1A3625]/10 text-[#1A3625] px-4 py-2 rounded-full text-sm font-medium hover:bg-[#1A3625]/20 transition-colors"
      data-testid="reorder-btn"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
      Son Siparişi Tekrarla
    </button>
  );
};
